import { LitElement, html } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Upload, Loader2, ShieldCheck, ShieldAlert } from 'lucide';
import QRCode from 'qrcode';
import { api } from '../api.js';
import { auth } from '../auth.js';
import { lucideIcon } from '../icon.js';
import { initials, displayName, avatarColor } from '../utils/avatar.js';
import { confirmDialog, alertDialog, promptDialog } from '../utils/confirm-dialog.js';
import { passwordMeetsRules, passwordChecklist } from '../utils/password-policy.js';

/**
 * /personal/settings — the logged-in user's own profile: name + avatar.
 * Name is saved with the "Opslaan" button; avatar upload/remove act immediately.
 * Every change dispatches `profile-updated` so the sidebar avatar/name refreshes.
 */
export class PersonalSettings extends LitElement {
  createRenderRoot() { return this; }

  static properties = {
    _firstName: { type: String, state: true },
    _lastName: { type: String, state: true },
    _email: { type: String, state: true },
    _avatarUpdatedAt: { type: String, state: true },
    _loading: { type: Boolean, state: true },
    _saving: { type: Boolean, state: true },
    _busyAvatar: { type: Boolean, state: true },
    _saved: { type: Boolean, state: true },
    _twoFA: { type: Object, state: true },
    _twoFAStep: { type: String, state: true },   // 'idle' | 'enrolling'
    _qrDataUrl: { type: String, state: true },
    _secret: { type: String, state: true },
    _code: { type: String, state: true },
    _twoFABusy: { type: Boolean, state: true },
    _twoFAError: { type: String, state: true },
    _pwCurrent: { type: String, state: true },
    _pwNew: { type: String, state: true },
    _pwConfirm: { type: String, state: true },
    _pwBusy: { type: Boolean, state: true },
    _pwError: { type: String, state: true },
    _pwSaved: { type: Boolean, state: true },
  };

  constructor() {
    super();
    this._firstName = '';
    this._lastName = '';
    this._email = '';
    this._avatarUpdatedAt = null;
    this._loading = true;
    this._saving = false;
    this._busyAvatar = false;
    this._saved = false;
    this._twoFA = null;         // { enabled, required }
    this._twoFAStep = 'idle';
    this._qrDataUrl = null;
    this._secret = '';
    this._code = '';
    this._twoFABusy = false;
    this._twoFAError = '';
    this._pwCurrent = '';
    this._pwNew = '';
    this._pwConfirm = '';
    this._pwBusy = false;
    this._pwError = '';
    this._pwSaved = false;
  }

  async _changePassword() {
    this._pwError = ''; this._pwSaved = false;
    if (!passwordMeetsRules(this._pwNew)) { this._pwError = 'Je nieuwe wachtwoord voldoet nog niet aan alle eisen.'; return; }
    if (this._pwNew !== this._pwConfirm) { this._pwError = 'De nieuwe wachtwoorden komen niet overeen.'; return; }
    this._pwBusy = true;
    try {
      await api.changePassword(this._pwCurrent, this._pwNew);
      this._pwCurrent = ''; this._pwNew = ''; this._pwConfirm = '';
      this._pwSaved = true;
      setTimeout(() => { this._pwSaved = false; }, 2500);
    } catch (e) {
      this._pwError = e.message || 'Wachtwoord wijzigen mislukt';
    } finally {
      this._pwBusy = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    this._loading = true;
    try {
      const p = await api.getPersonalProfile();
      this._firstName = p.firstName || '';
      this._lastName = p.lastName || '';
      this._email = p.email || '';
      this._avatarUpdatedAt = p.avatarUpdatedAt || null;
    } catch (e) {
      alertDialog({ title: 'Fout', message: `Profiel laden mislukt: ${e.message}` });
    } finally {
      this._loading = false;
    }
    try { this._twoFA = await api.get2faStatus(); } catch { this._twoFA = { enabled: false, required: false }; }
  }

  // ── Two-factor authentication ──────────────────────────────────────────────
  async _startEnroll() {
    this._twoFABusy = true; this._twoFAError = ''; this._code = '';
    try {
      const { otpauthUri, secret } = await api.setup2fa();
      this._secret = secret;
      this._qrDataUrl = await QRCode.toDataURL(otpauthUri, { width: 190, margin: 1 });
      this._twoFAStep = 'enrolling';
    } catch (e) {
      alertDialog({ title: 'Kon 2FA niet starten', message: e.message });
    } finally {
      this._twoFABusy = false;
    }
  }

  _cancelEnroll() {
    this._twoFAStep = 'idle'; this._qrDataUrl = null; this._secret = ''; this._code = ''; this._twoFAError = '';
  }

  async _confirmEnroll() {
    if (!/^\d{6}$/.test(this._code.trim())) { this._twoFAError = 'Voer de 6-cijferige code in.'; return; }
    this._twoFABusy = true; this._twoFAError = '';
    try {
      await api.enable2fa(this._code.trim());
      this._twoFA = { ...this._twoFA, enabled: true };
      this._cancelEnroll();
      alertDialog({ title: 'Ingeschakeld', message: 'Tweestapsverificatie staat nu aan. Je hebt bij volgende logins een code uit je authenticator-app nodig.' });
    } catch (e) {
      this._twoFAError = e.message || 'Ongeldige code';
    } finally {
      this._twoFABusy = false;
    }
  }

  async _disable2fa() {
    const code = await this._promptCode('Tweestapsverificatie deactiveren', 'Voer een code uit je authenticator-app in om te bevestigen.');
    if (code == null) return;
    this._twoFABusy = true;
    try {
      await api.disable2fa(code);
      this._twoFA = { ...this._twoFA, enabled: false };
    } catch (e) {
      alertDialog({ title: 'Uitschakelen mislukt', message: e.message });
    } finally {
      this._twoFABusy = false;
    }
  }

  async _promptCode(title, message) {
    const val = await promptDialog({ title, message, placeholder: '000000', confirmLabel: 'Bevestigen' });
    if (val == null) return null;
    const clean = String(val).trim();
    if (!/^\d{6}$/.test(clean)) { await alertDialog({ title: 'Ongeldig', message: 'Voer de 6-cijferige code in.' }); return null; }
    return clean;
  }

  _notify() { window.dispatchEvent(new CustomEvent('profile-updated')); }

  async _save() {
    this._saving = true; this._saved = false;
    try {
      await api.updatePersonalProfile({ firstName: this._firstName.trim(), lastName: this._lastName.trim() });
      this._notify();
      this._saved = true;
      setTimeout(() => { this._saved = false; }, 2500);
    } catch (e) {
      alertDialog({ title: 'Opslaan mislukt', message: e.message });
    } finally {
      this._saving = false;
    }
  }

  async _onAvatarPick(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alertDialog({ title: 'Te groot', message: 'Kies een afbeelding van maximaal 2 MB.' }); return; }
    this._busyAvatar = true;
    try {
      const res = await api.uploadAvatar(file);
      this._avatarUpdatedAt = res.avatarUpdatedAt || new Date().toISOString();
      this._notify();
    } catch (err) {
      alertDialog({ title: 'Upload mislukt', message: err.message });
    } finally {
      this._busyAvatar = false;
    }
  }

  async _removeAvatar() {
    const ok = await confirmDialog({ title: 'Avatar verwijderen?', message: 'Je profielfoto wordt verwijderd; er worden weer initialen getoond.', confirmLabel: 'Verwijderen', danger: true });
    if (!ok) return;
    this._busyAvatar = true;
    try {
      await api.deleteAvatar();
      this._avatarUpdatedAt = null;
      this._notify();
    } catch (err) {
      alertDialog({ title: 'Verwijderen mislukt', message: err.message });
    } finally {
      this._busyAvatar = false;
    }
  }

  _renderAvatar(size = 56) {
    const p = { firstName: this._firstName, lastName: this._lastName, email: this._email };
    const url = this._avatarUpdatedAt ? api.avatarUrl(auth.getUser()?.id, this._avatarUpdatedAt) : null;
    const box = `flex-shrink:0; width:${size}px; height:${size}px; border-radius:50%; overflow:hidden; display:flex; align-items:center; justify-content:center;`;
    if (url) return html`<span style="${box} background:var(--mv-bg,#e9ecef);"><img src=${url} alt="" style="width:100%; height:100%; object-fit:cover;" /></span>`;
    return html`<span style="${box} background:${avatarColor(this._email || displayName(p))}; color:#fff; font-weight:800; font-size:1.25rem;">${initials(p)}</span>`;
  }

  render() {
    const rowLabel = 'font-weight:700; font-size:0.92rem; color:var(--mv-text); margin-bottom:2px;';
    const rowDesc = 'font-size:0.82rem; color:var(--mv-text-sub,#6c757d); max-width:220px;';
    const field = 'width:100%; padding:0.65rem 0.9rem; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); font-size:0.92rem; color:var(--mv-text); background:var(--mv-bg); outline:none; box-sizing:border-box; font-family:var(--mv-font);';
    const rowWrap = 'display:flex; gap:24px; align-items:flex-start; padding:20px 22px; border-top:1px solid var(--mv-border);';
    const secBtn = 'display:inline-flex; align-items:center; gap:7px; padding:0.55rem 0.95rem; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; font-size:0.88rem; cursor:pointer; font-family:var(--mv-font);';

    return html`
      <div style="max-width:800px; margin:0 auto;">
        <h1 style="margin:0 0 4px; font-size:1.5rem; font-weight:700; color:var(--mv-text);">Profielinstellingen</h1>
        <div style="color:var(--mv-text-sub,#6c757d); font-size:0.92rem; margin-bottom:20px;">Beheer hoe je naam en avatar verschijnen voor anderen.</div>

        ${this._loading ? html`<div style="padding:2rem; text-align:center; color:var(--mv-text-sub,#6c757d);">Laden…</div>` : html`
          <div style="border:1px solid var(--mv-border); border-radius:var(--mv-radius); background:var(--mv-surface,#fff); overflow:hidden;">

            <div style="${rowWrap} border-top:none;">
              <div style="flex:1;">
                <div style=${rowLabel}>Avatar</div>
                <div style=${rowDesc}>Stel een avatar in voor je profiel.</div>
              </div>
              <div style="display:flex; align-items:center; gap:14px;">
                ${this._renderAvatar(56)}
                <label style="${secBtn} ${this._busyAvatar ? 'opacity:0.6; cursor:default;' : ''}">
                  ${this._busyAvatar ? unsafeSVG(lucideIcon(Loader2, 15)) : unsafeSVG(lucideIcon(Upload, 15))} Avatar uploaden
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden ?disabled=${this._busyAvatar} @change=${(e) => this._onAvatarPick(e)}>
                </label>
                ${this._avatarUpdatedAt ? html`<button style=${secBtn} ?disabled=${this._busyAvatar} @click=${() => this._removeAvatar()}>Verwijderen</button>` : ''}
              </div>
            </div>

            <div style=${rowWrap}>
              <div style="flex:1;"><div style=${rowLabel}>Voornaam</div></div>
              <div style="flex:1.6;"><input type="text" .value=${this._firstName} @input=${(e) => { this._firstName = e.target.value; }} placeholder="Voornaam" style=${field}></div>
            </div>

            <div style=${rowWrap}>
              <div style="flex:1;"><div style=${rowLabel}>Achternaam</div></div>
              <div style="flex:1.6;"><input type="text" .value=${this._lastName} @input=${(e) => { this._lastName = e.target.value; }} placeholder="Achternaam" style=${field}></div>
            </div>

            <div style=${rowWrap}>
              <div style="flex:1;">
                <div style=${rowLabel}>E-mailadres</div>
                <div style=${rowDesc}>Je e-mailadres is je inlognaam en kan hier niet aangepast worden.</div>
              </div>
              <div style="flex:1.6;"><input type="email" readonly .value=${this._email} style="${field} background:var(--mv-bg,#f1f3f5); color:var(--mv-text-sub,#6c757d); cursor:not-allowed;"></div>
            </div>

            <div style="display:flex; align-items:center; justify-content:flex-end; gap:14px; padding:16px 22px; border-top:1px solid var(--mv-border);">
              ${this._saved ? html`<span style="color:#15803d; font-size:0.88rem; font-weight:600;">Opgeslagen ✓</span>` : ''}
              <button class="btn btn-primary" ?disabled=${this._saving} @click=${() => this._save()}
                style="padding:0.6rem 1.4rem; border:none; border-radius:var(--mv-radius-sm); background:var(--mv-primary); color:#fff; font-weight:700; font-size:0.92rem; cursor:pointer; ${this._saving ? 'opacity:0.7;' : ''}">
                ${this._saving ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </div>
        `}

        ${this._loading ? '' : html`
          <h2 style="margin:32px 0 4px; font-size:1.25rem; font-weight:700; color:var(--mv-text);">Beveiliging</h2>
          <div style="color:var(--mv-text-sub,#6c757d); font-size:0.92rem; margin-bottom:20px;">Beheer je wachtwoord en tweestapsverificatie.</div>
          ${this._renderPasswordCard()}
          ${this._renderTwoFactorCard()}
        `}
      </div>
    `;
  }

  _renderPasswordCard() {
    const rowLabel = 'font-weight:700; font-size:0.92rem; color:var(--mv-text); margin-bottom:2px;';
    const rowDesc = 'font-size:0.82rem; color:var(--mv-text-sub,#6c757d); max-width:220px;';
    const field = 'width:100%; padding:0.65rem 0.9rem; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); font-size:0.92rem; color:var(--mv-text); background:var(--mv-bg); outline:none; box-sizing:border-box; font-family:var(--mv-font);';
    const rowWrap = 'display:flex; gap:24px; align-items:flex-start; padding:20px 22px; border-top:1px solid var(--mv-border);';
    const submitOnEnter = (e) => { if (e.key === 'Enter') this._changePassword(); };

    return html`
      <div style="border:1px solid var(--mv-border); border-radius:var(--mv-radius); background:var(--mv-surface,#fff); overflow:hidden; margin-bottom:22px;">
        <div style="${rowWrap} border-top:none;">
          <div style="flex:1;"><div style=${rowLabel}>Huidig wachtwoord</div></div>
          <div style="flex:1.6;"><input type="password" autocomplete="current-password" .value=${this._pwCurrent}
            @input=${(e) => { this._pwCurrent = e.target.value; this._pwError = ''; }} placeholder="Huidig wachtwoord" style=${field}></div>
        </div>
        <div style=${rowWrap}>
          <div style="flex:1;"><div style=${rowLabel}>Nieuw wachtwoord</div><div style=${rowDesc}>Kies een sterk, uniek wachtwoord.</div></div>
          <div style="flex:1.6;">
            <input type="password" autocomplete="new-password" .value=${this._pwNew}
              @input=${(e) => { this._pwNew = e.target.value; this._pwError = ''; }} placeholder="Nieuw wachtwoord" style=${field}>
            ${this._pwNew ? passwordChecklist(this._pwNew) : ''}
          </div>
        </div>
        <div style=${rowWrap}>
          <div style="flex:1;"><div style=${rowLabel}>Bevestig nieuw wachtwoord</div></div>
          <div style="flex:1.6;"><input type="password" autocomplete="new-password" .value=${this._pwConfirm}
            @input=${(e) => { this._pwConfirm = e.target.value; this._pwError = ''; }} @keydown=${submitOnEnter} placeholder="Herhaal nieuw wachtwoord" style=${field}></div>
        </div>
        <div style="display:flex; align-items:center; justify-content:flex-end; gap:14px; padding:16px 22px; border-top:1px solid var(--mv-border);">
          ${this._pwError ? html`<span style="color:#dc2626; font-size:0.85rem; margin-right:auto;">${this._pwError}</span>` : ''}
          ${this._pwSaved ? html`<span style="color:#15803d; font-size:0.88rem; font-weight:600;">Wachtwoord gewijzigd ✓</span>` : ''}
          <button ?disabled=${this._pwBusy || !this._pwCurrent || !this._pwNew || !this._pwConfirm} @click=${() => this._changePassword()}
            style="padding:0.6rem 1.4rem; border:none; border-radius:var(--mv-radius-sm); background:var(--mv-primary); color:#fff; font-weight:700; font-size:0.92rem; cursor:pointer; ${this._pwBusy || !this._pwCurrent || !this._pwNew || !this._pwConfirm ? 'opacity:0.6;' : ''}">
            ${this._pwBusy ? 'Bezig…' : 'Wachtwoord wijzigen'}
          </button>
        </div>
      </div>
    `;
  }

  _renderTwoFactorCard() {
    const enabled = !!this._twoFA?.enabled;
    const field = 'width:100%; padding:0.65rem 0.9rem; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); font-size:1.1rem; letter-spacing:4px; text-align:center; color:var(--mv-text); background:var(--mv-bg); outline:none; box-sizing:border-box; font-family:var(--mv-font);';
    const primaryBtn = 'padding:0.6rem 1.3rem; border:none; border-radius:var(--mv-radius-sm); background:var(--mv-primary); color:#fff; font-weight:700; font-size:0.9rem; cursor:pointer; font-family:var(--mv-font);';
    const secBtn = 'display:inline-flex; align-items:center; gap:7px; padding:0.55rem 0.95rem; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; font-size:0.88rem; cursor:pointer; font-family:var(--mv-font);';
    const pill = (bg, fg, label, icon) => html`<span style="display:inline-flex; align-items:center; gap:5px; font-size:0.74rem; font-weight:800; color:${fg}; background:${bg}; padding:3px 10px; border-radius:20px;">${unsafeSVG(lucideIcon(icon, 13))}${label}</span>`;

    return html`
      <div style="margin-top:22px; border:1px solid var(--mv-border); border-radius:var(--mv-radius); background:var(--mv-surface,#fff); overflow:hidden;">
        <div style="display:flex; gap:24px; align-items:center; padding:20px 22px;">
          <div style="flex:1;">
            <div style="font-weight:700; font-size:0.98rem; color:var(--mv-text); margin-bottom:3px; display:flex; align-items:center; gap:9px;">
              Tweestapsverificatie (2FA)
              ${enabled
                ? pill('rgba(16,185,129,0.12)', '#15803d', 'INGESCHAKELD', ShieldCheck)
                : pill('rgba(234,179,8,0.14)', '#b45309', 'UIT', ShieldAlert)}
            </div>
            <div style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d); max-width:440px; line-height:1.5;">
              Beveilig je account met een extra code uit een authenticator-app (Google Authenticator, Microsoft Authenticator, 1Password, Authy en dergelijke).
            </div>
          </div>
          <div style="flex-shrink:0;">
            ${enabled
              ? html`<button style="${secBtn} border-color:var(--mv-danger,#dc3545); color:var(--mv-danger,#dc3545);" ?disabled=${this._twoFABusy} @click=${() => this._disable2fa()}>${this._twoFABusy ? 'Bezig…' : 'Deactiveren'}</button>`
              : (this._twoFAStep === 'idle'
                  ? html`<button style=${primaryBtn} ?disabled=${this._twoFABusy} @click=${() => this._startEnroll()}>${this._twoFABusy ? 'Bezig…' : 'Inschakelen'}</button>`
                  : '')}
          </div>
        </div>

        ${this._twoFAStep === 'enrolling' ? html`
          <div style="border-top:1px solid var(--mv-border); padding:22px; display:flex; gap:26px; flex-wrap:wrap; align-items:flex-start;">
            <div style="text-align:center;">
              ${this._qrDataUrl ? html`<img src=${this._qrDataUrl} alt="QR-code" style="width:190px; height:190px; border:1px solid var(--mv-border); border-radius:10px;" />` : ''}
            </div>
            <div style="flex:1; min-width:240px; display:flex; flex-direction:column; gap:14px;">
              <div style="font-size:0.87rem; color:var(--mv-text); line-height:1.5;"><strong>1.</strong> Scan de QR-code met je authenticator-app.</div>
              <div>
                <div style="font-size:0.87rem; color:var(--mv-text); line-height:1.5; margin-bottom:6px;"><strong>2.</strong> Kun je niet scannen? Voer deze sleutel handmatig in:</div>
                <div style="font-family:monospace; font-size:0.82rem; background:var(--mv-bg); border:1px solid var(--mv-border); border-radius:6px; padding:8px 10px; word-break:break-all;">${this._secret}</div>
              </div>
              <div>
                <div style="font-size:0.87rem; color:var(--mv-text); line-height:1.5; margin-bottom:6px;"><strong>3.</strong> Voer de 6-cijferige code uit de app in:</div>
                <input type="text" inputmode="numeric" maxlength="6" placeholder="000000"
                  .value=${this._code}
                  @input=${(e) => { this._code = e.target.value.replace(/\D/g, ''); this._twoFAError = ''; }}
                  @keydown=${(e) => { if (e.key === 'Enter') this._confirmEnroll(); }}
                  style="${field} max-width:220px;">
                ${this._twoFAError ? html`<div style="color:#dc2626; font-size:0.82rem; margin-top:8px;">${this._twoFAError}</div>` : ''}
              </div>
              <div style="display:flex; gap:10px;">
                <button style=${primaryBtn} ?disabled=${this._twoFABusy} @click=${() => this._confirmEnroll()}>${this._twoFABusy ? 'Bezig…' : 'Bevestigen'}</button>
                <button style=${secBtn} ?disabled=${this._twoFABusy} @click=${() => this._cancelEnroll()}>Annuleren</button>
              </div>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('personal-settings', PersonalSettings);
